import React, {
  FunctionComponent,
  useState,
  useEffect,
  SyntheticEvent,
  useContext,
} from "react";
import {
  ActionType,
  PrescriptionDrug,
  DrugDosage,
  DrugPackage,
  PrescriptionFrequency,
  PharmacyType,
} from "../../@types";
import { getDrugDosages } from "../../api";
import {
  Alert,
  Button,
  Dropdown,
  TextField,
  Dialog,
} from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../helpers/intlHooks";
import { AppStore } from "../../app/store";
import {
  displayPackage,
  displayDrug,
  uppercaseFirst,
} from "../../helpers/prescriptionHelpers";
import { CONSTANTS, logError, sendTealiumEvent } from "../../helpers";
import { PrescriptionsView } from "./ManagePrescriptionsPage";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";

interface EditPrescriptionProps {
  drug: { is_generic: boolean; rxcui: string; name: string };
  prescriptionIndex: number;
  viewList: (clearRecentlyAdded: boolean) => void;
  resetPrescriptionIndex: () => void;
  goToPharmacySelection: () => void;
  resetModeOnError: () => void;
  addPrescriptions: () => void;
  previousMode: PrescriptionsView;
}

// This value will be shared with the test module, to test whether the dialog is shown
export const drugCantBeAddedErrorDialogTitleKey =
  "prescription.error.drug_cant_be_added";

const EditPrescription: FunctionComponent<EditPrescriptionProps> = ({
  drug,
  prescriptionIndex,
  viewList,
  resetPrescriptionIndex,
  goToPharmacySelection,
  resetModeOnError,
  addPrescriptions,
  previousMode,
}) => {
  const t = useTranslate();
  const { dispatch, state } = useContext(AppStore.AppContext);
  const flags = useFlags();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [drugAvailable, setDrugAvailable] = useState(true);
  const [dosage, setDosage] = useState({} as DrugDosage);
  const [dosages, setDosages] = useState({} as DrugDosage[]);
  const [pack, setPack] = useState({} as DrugPackage);
  const [prescription, setPrescription] = useState({
    frequency:
      state.pharmacyType === PharmacyType.MAIL
        ? PrescriptionFrequency.DAYS_90
        : PrescriptionFrequency.DAYS_30,
    quantity: "",
  } as Partial<PrescriptionDrug>);
  const [submitted, setSubmitted] = useState(false);

  const drugFrequencyLabels = CONSTANTS.prescriptionFrequencyLabels;
  const drugFrequencies = Object.keys(drugFrequencyLabels).map(key => ({
    label: t(drugFrequencyLabels[key]),
    value: key,
  }));

  const getDefaultQuantity = (pack: DrugPackage): number | "" => {
    const defaultQuantity =
      state.pharmacyType === PharmacyType.MAIL
        ? pack.default_quantity_90
        : pack.default_quantity_30;

    return defaultQuantity || "";
  };

  const setDrugPackage = (
    pack: DrugPackage,
    useDefaultQuantity = true
  ): void => {
    setPack(pack);

    if (useDefaultQuantity) {
      setPrescription({
        ...prescription,
        quantity: getDefaultQuantity(pack),
      });
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(false);

    getDrugDosages(drug.rxcui, state.year)
      .then(result => {
        if (result && result.dosages && result.dosages.length > 0) {
          setLoading(false);
          setDrugAvailable(true);
          setDosages(result.dosages);

          if (prescriptionIndex !== -1) {
            const prescription = state.prescriptions[prescriptionIndex];
            setPrescription(prescription);

            const dosage = result.dosages.find(
              d =>
                d.form === prescription.form &&
                d.strength === prescription.strength &&
                d.strength_units === prescription.strength_units &&
                d.rxcui === prescription.rxcui
            );
            setDosage(dosage || result.dosages[0]);
          } else if (result.commonDosage) {
            setDosage(result.commonDosage);
            const pack = result.commonDosage.packages[0];

            setDrugPackage(pack);
          }
        } else {
          /**
           * Unclear as to whether these should be generic `Error`s or
           * `ApiResponseError`s
           */
          // If there are no dosages, throw a specific error
          if (result?.dosages && result.dosages.length === 0) {
            setDrugAvailable(false);
            throw new Error(
              `💊 No dosages returned for ${drug.name} - ${drug.rxcui}`
            );
          }
          // Be sure to throw an Error object and not a string
          throw new Error("API Error");
        }
      })
      .catch(e => {
        /**
         * `setLoading(false)` and `setError(true)` should be called here and not
         * along with `throw`s (which will be caught here)
         */
        setLoading(false);
        setError(true);
        logError("Could not get drug dosages (EditPrescription)", e);
      });
  }, []);

  const showDosagePackages =
    dosage &&
    dosage.packages &&
    dosage.packages.length &&
    dosage.packages.every(p => p.use_quantity);

  useEffect(() => {
    if (dosage && dosage.packages && dosage.packages.length) {
      if (prescriptionIndex === -1) {
        setDrugPackage(dosage.packages[0]);
      } else {
        const pack = dosage.packages.find(p => p.ndc === prescription.ndc);

        setDrugPackage(pack || dosage.packages[0], false);
      }
    }
  }, [dosage]);

  const makeDosageValueString = (dosage: DrugDosage): string =>
    dosage.form && dosage.rxcui
      ? `${dosage.strength || ""}|${dosage.strength_units || ""}|${
          dosage.form
        }|${dosage.rxcui}`
      : "";

  if (loading) {
    return <div className="ds-u-margin-y--6">{t("app.loading")}...</div>;
  } else if (!dosage || error) {
    // Store actions components in an array. Add second button if there are dosages.
    const actions = [
      <Button
        variation="primary"
        key="add-generic-button"
        onClick={(): void => {
          // istanbul ignore next: tested elsewhere
          sendTealiumEvent("Find Plans - Add Drugs", "Add Another Drug");
          resetModeOnError();
        }}
      >
        {drugAvailable
          ? t("prescription.add_other_drugs")
          : t("prescription.return_to_drug_search")}
      </Button>,
    ];
    drugAvailable &&
      actions.push(
        <Button
          key="add-brand-button"
          variation="transparent"
          onClick={(): void => goToPharmacySelection()}
        >
          {t("continue")}
        </Button>
      );
    return (
      <Dialog
        onExit={(): void => viewList(false)}
        heading={
          drugAvailable
            ? t("prescription.error_dialog_title")
            : t(drugCantBeAddedErrorDialogTitleKey)
        }
        headerClassName="AddPrescription__promptHeader"
        actions={actions}
      >
        {drugAvailable && <p>{t("prescription.error_dialog_message")}</p>}
      </Dialog>
    );
  } else {
    return (
      <form className="EditPrescription e2e-drug-form">
        {submitted && (!dosage.rxcui || !prescription.quantity) && (
          <Alert
            variation="error"
            className="ds-u-margin-bottom--3"
            heading={t("prescription.error.needs_attention")}
          >
            {t("prescription.error.complete_required_info")}
          </Alert>
        )}
        <h2 className="ds-u-margin--0 ds-u-margin-top--1 ds-u-margin-bottom--4">
          {uppercaseFirst(drug.name)}
        </h2>

        {!!dosages.length && (
          <Dropdown
            label={t("prescription.dosage")}
            id="drug-select-dosage"
            name="dosage"
            fieldClassName="e2e-drug-select-dosage"
            onChange={(event: SyntheticEvent<HTMLSelectElement>): void => {
              const dosageString = event.currentTarget.value;
              const [
                strength,
                strength_units,
                form,
                rxcui,
              ] = dosageString.split("|");

              const dosage = dosages.find(
                d =>
                  d.form === form &&
                  d.strength === strength &&
                  d.strength_units === strength_units &&
                  d.rxcui === rxcui
              );

              setDosage(dosage || ({} as DrugDosage));
            }}
            value={makeDosageValueString(dosage)}
            options={[]}
            errorMessage={
              submitted && !dosage.rxcui
                ? t("prescription.select_dosage.error")
                : undefined
            }
          >
            <option value="">{t("prescription.select_dosage")}</option>
            {dosages.map((d, index) => (
              <option
                value={makeDosageValueString(d)}
                className={`e2e-drug-dosage-option-${index}`}
                key={`${d.rxcui}-${index}`}
              >
                {displayDrug(t, {
                  form: d.form,
                  strength: d.strength,
                  strength_units: d.strength_units,
                  name: d.has_duplicate_name ? d.name : "",
                })}
              </option>
            ))}
          </Dropdown>
        )}

        {showDosagePackages && (
          <Dropdown
            label={t("prescription.package")}
            id="drug-select-package"
            name="package"
            onChange={(event: SyntheticEvent<HTMLSelectElement>): void => {
              const pack = dosage.packages.find(
                p => p.ndc === event.currentTarget.value
              );

              setDrugPackage(pack || ({} as DrugPackage));
            }}
            value={pack.ndc}
            options={[]}
          >
            {dosage.packages.map(dosagePack => (
              <option value={dosagePack.ndc} key={dosagePack.ndc}>
                {displayPackage(t, {
                  form: dosage.form,
                  package_size: dosagePack.size.toString(10),
                  package_units: dosagePack.unit,
                  package_quantity: dosagePack.quantity.toString(10),
                  package_type: dosagePack.type,
                })}
              </option>
            ))}
          </Dropdown>
        )}

        <div className="ds-u-display--flex ds-u-align-items--end">
          <TextField
            label={t("prescription.quantity")}
            id="drug-select-quantity"
            name="quantity"
            value={prescription.quantity}
            className="e2e-drug-quantity"
            numeric
            min="1"
            size="small"
            onChange={(event: SyntheticEvent<HTMLInputElement>): void => {
              const { value } = event.currentTarget;

              const parsedValue = parseInt(value, 10);

              const quantity = isNaN(parsedValue) ? "" : Math.abs(parsedValue);

              setPrescription({
                ...prescription,
                quantity,
              });
            }}
            errorMessage={
              submitted && !prescription.quantity
                ? t("prescription.quantity.error")
                : undefined
            }
          />
          <div className="ds-u-clearfix">
            <Dropdown
              label={t("prescription.frequency")}
              id="drug-select-frequency"
              value={prescription.frequency}
              className="ds-u-margin-left--3"
              name="frequency"
              onChange={(event: SyntheticEvent<HTMLSelectElement>): void =>
                setPrescription({
                  ...prescription,
                  frequency: event.currentTarget.value as PrescriptionFrequency,
                })
              }
              options={drugFrequencies}
            />
          </div>
        </div>

        <div className="EditPrescription__footer">
          <Button
            variation="primary"
            type="submit"
            className="e2e-drug-add-to-list ds-u-margin-right--2 ds-u-sm-margin-bottom--0 mct-u-width--full mct-u-sm-width--auto"
            onClick={(
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>
            ): boolean => {
              event.preventDefault();
              setSubmitted(true);

              if (!dosage.rxcui || !prescription.quantity) {
                return false;
              }

              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - Add Drugs", "Add Drug to List");

              const drugPayload: PrescriptionDrug = {
                rxcui: dosage.rxcui,
                name: dosage.has_duplicate_name ? dosage.name : drug.name,
                ndc: pack.ndc,
                form: dosage.form,
                strength: dosage.strength,
                strength_units: dosage.strength_units,
                package_type: pack.type,
                package_size: pack.size.toString(10),
                package_units: pack.unit,
                frequency: prescription.frequency as Required<
                  PrescriptionFrequency
                >,
                package_quantity: pack.quantity.toString(10),
                quantity: prescription.quantity,
                is_generic: drug.is_generic,
                use_package_quantity: pack.use_quantity,
              };

              if (prescriptionIndex === -1) {
                createGa4Event({
                  event_name: Ga4Event.DRUG_ADDED,
                  drug_type: drug.is_generic ? "generic" : "brand",
                  state,
                  flags,
                });

                dispatch({
                  type: ActionType.ADD_PRESCRIPTION,
                  payload: drugPayload,
                });
              } else {
                createGa4Event({
                  event_name: Ga4Event.DRUG_EDITED,
                  drug_type: drug.is_generic ? "generic" : "brand",
                  state,
                  flags,
                });

                dispatch({
                  type: ActionType.EDIT_PRESCRIPTION,
                  payload: {
                    index: prescriptionIndex,
                    drug: drugPayload,
                  },
                });

                resetPrescriptionIndex();
              }

              viewList(false);
              return true;
            }}
          >
            {previousMode === PrescriptionsView.ADD
              ? t("prescription.add_to_my_drug_list")
              : t("prescription.update_this_drug")}
          </Button>
          <Button
            onClick={(): void => {
              if (previousMode === PrescriptionsView.ADD) {
                addPrescriptions();
              } else {
                viewList(true);
              }
            }}
            className="EditPrescription__cancelButton mct-u-width--full mct-u-sm-width--auto ds-u-margin-top--2 ds-u-sm-margin-top--0"
          >
            {t("cancel")}
          </Button>
        </div>
      </form>
    );
  }
};

export default EditPrescription;
